// app/products/page.tsx
'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { Fragment, useEffect } from 'react';
import { storesApi } from '../../../services/stores.service';
import { BasicProductDataResponse } from '@/types/catalog';
import { ProductCard } from './ProductCard';
import Loading from '@/components/ui/loading';
import Container from '@/components/ui/container';

type Props = {
  branchId: string;

  initialProducts: BasicProductDataResponse[];
  subIndustry?: string;
  category?: string;
  price?: string;
  query?: string;
  subIndustryId?: string;
  storeIdfromBranch?: string;
};
export default function InfintyProducts({
  branchId,
  initialProducts,
  category,
  price,
  query,
  subIndustry,
  subIndustryId,
  storeIdfromBranch,
}: Props) {
  const { ref, inView } = useInView();

  // Fetch products using useInfiniteQuery
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } = useInfiniteQuery({
    queryKey: ['products', branchId, subIndustry, category, price, query, subIndustryId],
    queryFn: async ({ pageParam = 1 }) => {
      const params = {
        page: pageParam.toString(),
        search: query,
        industryId: subIndustryId?.toString(),
        ...(query && { storeId: storeIdfromBranch?.toString() }),
      };

      try {
        if (pageParam > 1) {
          return [];
        }
        const response = await storesApi.getAllProductsForBranch(branchId, params);
        return response as BasicProductDataResponse[];
      } catch (error) {
        console.error('Error fetching products:', error);
        return [];
      }
    },
    initialData: {
      pages: [initialProducts], // Set the first page as initial data
      pageParams: [1],
    },

    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length === 0) return undefined;
      return allPages.length + 1;
    },
    initialPageParam: 1, // Initial page number
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Trigger fetchNextPage when the observer is in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (status === 'error') {
    return <div>Error fetching products</div>;
  }

  const allProducts = data?.pages.flat() || [];

  return (
    <div>
      <Container className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-5 lg:grid-cols-4">
        {allProducts.map((item, index) => (
          // Use a more stable key if possible (item.id instead of index)
          <div key={item.integrationId || index}>
            <ProductCard productDetails={item} />
          </div>
        ))}
      </Container>

      {/* Observer element */}
      <div ref={ref} className="mt-4 h-5 text-center">
        {isFetchingNextPage ? <Loading /> : hasNextPage ? 'Load More' : null}
      </div>
    </div>
  );
}
